import React, {useState} from 'react';
import {Modal} from "../../../utils/modal/Modal";
import {materialError} from "./materialError";
import {videoDurationSec} from "./videoDuration";
import {useDispatch} from "react-redux";
import {toast} from "react-toastify";
import {materialSaveAction, materialUploadAction} from "../../../actions/materialAction";
import {InputProfile} from "../../../utils/input/InputProfile";

/**
 * Заведение и правка материала.
 *
 * Видеозапись или пособие: приложение по этому полю решает, плеер тут или
 * системный просмотрщик. Ошибка стоит дорого — пособие, названное видеозаписью,
 * уедет в плеер и не заиграет.
 *
 * Длительность и дата записи нужны видеозаписи: под ней в приложении стоит
 * «Видеозапись · 12 мая 2026 · 24 мин». У пособия их не бывает, и поля прячутся.
 *
 * Файл выбирается здесь же, хотя заливается вторым шагом: материал должен сначала
 * появиться, файл кладётся к нему. Человеку об этом знать незачем — он заводит
 * материал вместе с файлом, а порядок действий наше дело.
 *
 * Адрес рядом — для материалов, которые лежат не у нас: перенесённые из GetCourse
 * живут своим постоянным адресом, и заливать их копию к себе незачем.
 */
export function MaterialModal({item, stageId, trigger, triggerWrap, onSuccess}) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState(item ? item.kind : 'video');
  const [title, setTitle] = useState(item ? item.title : '');
  const [position, setPosition] = useState(item && item.order ? String(item.order) : '');
  const [minutes, setMinutes] = useState(item && item.duration_sec ? String(Math.round(item.duration_sec / 60)) : '');
  const [recordedAt, setRecordedAt] = useState(item && item.recorded_at ? item.recorded_at.substring(0, 10) : '');
  const [url, setUrl] = useState(item ? (item.url || '') : '');
  const [previewUrl, setPreviewUrl] = useState(item && item.preview_url ? item.preview_url : '');
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const dispatch = useDispatch();

  const handleOpen = (value) => {
    if (loading) {
      return;
    }
    if (value) {
      setKind(item ? item.kind : 'video');
      setTitle(item ? item.title : '');
      setPosition(item && item.order ? String(item.order) : '');
      setMinutes(item && item.duration_sec ? String(Math.round(item.duration_sec / 60)) : '');
      setRecordedAt(item && item.recorded_at ? item.recorded_at.substring(0, 10) : '');
      setUrl(item ? (item.url || '') : '');
      setPreviewUrl(item && item.preview_url ? item.preview_url : '');
      setFile(null);
      setError('');
    }
    setOpen(value);
  };

  /**
   * Выбрали файл — заодно узнаём длительность.
   *
   * Заполняем только пустое поле: если куратор уже вписал число, оно его, и
   * затирать его файлом нельзя. Длительность нужна клиентке в карточке и в
   * списке — «Видеозапись · 24 мин».
   */
  const handleFileChange = (e) => {
    const chosen = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setFile(chosen);

    if (!chosen || minutes.trim() !== '') {
      return;
    }
    videoDurationSec(chosen).then((seconds) => {
      if (seconds) {
        setMinutes(String(Math.max(1, Math.round(seconds / 60))));
      }
    });
  };

  const handleSubmit = () => {
    if (loading) {
      return false;
    }
    if (title.trim() === '') {
      setError('title');
      return false;
    }
    setError('');
    setLoading(true);

    dispatch(materialSaveAction({
      id: item ? item.id : '',
      stage_id: item ? item.stage_id : stageId,
      kind,
      title: title.trim(),
      order: Number(position) || 0,
      // Минуты человеку, секунды серверу: в приложении стоит «24 мин», и вводить
      // 1440 секунд, чтобы получить эту строку, никто не должен
      duration_sec: kind === 'video' && minutes ? Number(minutes) * 60 : null,
      recorded_at: kind === 'video' && recordedAt ? recordedAt + 'T00:00:00Z' : null,
      url: url.trim(),
      preview_url: previewUrl.trim() ? previewUrl.trim() : null,
    })).then((saved) => {
      // Файл кладётся к уже заведённому материалу: раньше его класть некуда
      if (!file) {
        setLoading(false);
        setOpen(false);
        onSuccess();
        return;
      }
      setUploading(true);
      return dispatch(materialUploadAction((saved && saved.id) || (item && item.id), file, '')).then(() => {
        setLoading(false);
        setUploading(false);
        setOpen(false);
        onSuccess();
      });
    }).catch((e) => {
      setLoading(false);
      setUploading(false);
      // Материал мог завестись, а файл не долететь: говорим об этом прямо, иначе
      // человек нажмёт «Сохранить» второй раз и заведёт материал дважды
      toast.error(materialError(e), {position: toast.POSITION.BOTTOM_RIGHT});
      onSuccess();
    });
  };

  return <Modal isStatic={true} open={open} handleOpen={handleOpen} trigger={trigger} triggerWrap={triggerWrap}>
    <React.Fragment>
      <div className="modal__title">{item ? "Правка материала" : "Новый материал"}</div>
      <div className="modal__wrap">
        <div className="profile__group">
          <p className="profile__help help">Что это</p>
          <select className="profile__input input" value={kind} disabled={loading}
                  onChange={(e) => setKind(e.target.value)}>
            <option value="video">Видеозапись</option>
            <option value="pdf">Пособие</option>
          </select>
        </div>
        <InputProfile type="text" title="Название" name="title"
                      placeholder="Например: Часть 1. Начинаем ступень «Детокс»"
                      value={title} error={error === 'title'} disabled={loading}
                      onChange={(e) => setTitle(e.target.value)}/>
        {/* Из этого номера собирается плейсхолдер: {detox_2}. Он же — порядок
            в списке у клиентки, поэтому число одно, а не два */}
        <InputProfile type="number" title="Порядок в ступени" name="order" placeholder="1"
                      value={position} disabled={loading}
                      onChange={(e) => setPosition(e.target.value)}/>
        <p className="profile__help help">
          По этому номеру собирается плейсхолдер материала для сообщения: второй материал ступени
          «Детокс» — это {'{detox_2}'}. Меняете номер — меняется и плейсхолдер, а тексты, где он
          уже написан, начнут указывать на другой материал.
        </p>
        {kind === 'video' && <InputProfile type="number" title="Длительность, минут" name="minutes" placeholder="24"
                                           value={minutes} disabled={loading}
                                           onChange={(e) => setMinutes(e.target.value)}/>}
        {kind === 'video' && <InputProfile type="date" title="Дата записи" name="recorded_at" placeholder=""
                                           value={recordedAt} disabled={loading}
                                           onChange={(e) => setRecordedAt(e.target.value)}/>}
        <div className="profile__group">
          <p className="profile__help help">Файл материала</p>
          <input type="file" name="file" className="profile__input input" disabled={loading}
                 onChange={handleFileChange}/>
          {item && item.has_file && !file &&
            <p className="profile__help help">Файл уже есть — выберите новый, только если нужно заменить</p>}
        </div>
        <InputProfile type="text" title="Или адрес, если файл лежит не у нас" name="url"
                      placeholder="Например, ссылка на видео в GetCourse"
                      value={url} disabled={loading}
                      onChange={(e) => setUrl(e.target.value)}/>
        <InputProfile type="text" title="Обложка" name="preview_url"
                      placeholder="Адрес картинки; можно оставить пустым"
                      value={previewUrl} disabled={loading}
                      onChange={(e) => setPreviewUrl(e.target.value)}/>
      </div>
      <div className="modal__btns">
        <button type="button" className="modal__btn btn" onClick={handleSubmit} disabled={loading}>
          {uploading ? 'Заливается…' : 'Сохранить'}
        </button>
      </div>
    </React.Fragment>
  </Modal>;
}
